import * as Sentry from "@sentry/react";
import posthog from "posthog-js";
import { useSelector } from "react-redux";

import { QuestionDependency, QuestionnaireContext } from "@types-runway/questionnaire";
import { RootState } from "@types-runway/state";
import { User } from "@types-runway/user";
import { ANALYTICS_EVENTS } from "@utils/analytics/events";

// Module-level Set: persists across re-renders, resets on page reload.
// Keyed on userId so multi-patient sessions each fire once per user.
const firedNullDobWarnings = new Set<string>();

export const useParentalConsentMonitoring = () => {
  const userId = useSelector<RootState, User["id"]>((state) => state.user.data.id);
  const dateOfBirth = useSelector<RootState, User["date_of_birth"]>(
    (state) => state.user.data.date_of_birth
  );

  const reportUnresolvableDob = (
    dependency: QuestionDependency,
    context: QuestionnaireContext
  ): void => {
    if (dependency.type !== "conditional" || dependency.conditional !== "dob") return;
    if (dateOfBirth || context.questions?.["dob"]?.answer || firedNullDobWarnings.has(userId)) return;

    firedNullDobWarnings.add(userId);
    const monitoringContext = { userId };
    posthog.capture(ANALYTICS_EVENTS.PARENTAL_CONSENT_SHOWN_UNRESOLVABLE_DOB, monitoringContext);
    Sentry.captureMessage("Parental consent shown with unresolvable DOB", {
      level: "warning",
      extra: monitoringContext,
    });
  };

  return { reportUnresolvableDob };
};
